import {
  AppBar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import AccountMenu from "../../components/auth/AccountMenu";
import { siteName } from "../../config";
import { paths } from "../../paths";
import {
  Download,
  Gavel,
  Help,
  Menu,
  PersonOff,
  Policy,
} from "@mui/icons-material";
const drawerWidth = 240;

interface LinkItemProps {
  to: string;
  text: string;
  icon: JSX.Element;
}

const LinkItem: FC<LinkItemProps> = ({ to, text, icon }) => {
  const { pathname } = useLocation();

  const selected = useMemo(
    () =>
      to === paths.index
        ? pathname === to
        : pathname.split("/").includes(to.replace("/", "")),
    [to, pathname],
  );
  return (
    <ListItem disablePadding>
      <ListItemButton component={Link} to={to} selected={selected}>
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText primary={text} />
      </ListItemButton>
    </ListItem>
  );
};

const MainLayout: FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const { pathname } = useLocation();
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const drawer = (
    <div>
      <Toolbar />
      <Divider />
      <List>
        <LinkItem
          to={paths.download}
          text="הורדת האפליקציה"
          icon={<Download />}
        />
        <LinkItem
          to={paths.auth.deleteMyAccount}
          text="מחיקת חשבון"
          icon={<PersonOff />}
        />
        <LinkItem to={paths.privacy} text="מדיניות פרטיות" icon={<Policy />} />
        <LinkItem to={paths.terms} text="תנאי שימוש" icon={<Gavel />} />
        <LinkItem to={paths.support} text="תמיכה" icon={<Help />} />
      </List>
    </div>
  );

  return (
    <Box
      sx={(theme) => ({
        direction: theme.direction,
      })}
    >
      <AppBar
        sx={(theme) => ({
          zIndex: { sm: theme.zIndex.drawer + 1 },
        })}
      >
        <Toolbar>
          <Stack
            direction="row"
            justifyContent="space-between"
            width="100%"
            alignItems={"center"}
          >
            <Stack direction="row" gap={2} alignItems={"center"}>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ display: { sm: "none" } }}
              >
                <Menu />
              </IconButton>
              <Typography
                sx={{
                  fontSize: {
                    sm: "1.5rem",
                  },
                }}
              >
                {siteName}
              </Typography>
            </Stack>
            <AccountMenu />
          </Stack>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        <Drawer
          variant="temporary"
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          anchor="right"
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mr: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default MainLayout;
